import { useState, useMemo, useCallback } from 'react';

import {
  Box,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Image,
  Spinner,
  Flex,
  HStack,
} from '@chakra-ui/react';

import {
  DownloadIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons';

import { Document, Page, pdfjs } from 'react-pdf';

import type { APIPolicyOverviewApplicationFile } from 'types/api.types';

import {
  getUserName,
  isApiUrl,
  downloadFileWithAuth,
} from 'views/policy_overview/utils';

import Storage from 'storage';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const FilePreviewModal = ({
  file,
  onClose,
  onNextFile,
  onPrevFile,
  canDownload = true,
}: {
  file: APIPolicyOverviewApplicationFile | null;
  onClose: () => void;
  onNextFile?: () => void;
  onPrevFile?: () => void;
  canDownload?: boolean;
}) => {
  const [numPages, setNumPages] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const pdfOptions = useMemo(
    () => ({
      httpHeaders: {
        User: Storage.get('user')?.token,
      },
    }),
    [],
  );

  const handleDownload = useCallback(async () => {
    if (!file?.file) return;
    if (isApiUrl(file.file)) {
      setIsDownloading(true);
      try {
        await downloadFileWithAuth(file.file, file.name);
      } catch {
        console.error('Download failed');
      } finally {
        setIsDownloading(false);
      }
    } else {
      const link = document.createElement('a');
      link.href = file.file;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [file]);

  if (!file) return null;

  const isPDF = file.name.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name || '');
  const fileUrl = file.file || undefined;

  return (
    <Modal isOpen onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {file.name || 'File Preview'}
          <Text fontSize="sm" color="gray.500" mt={1}>
            {`${file.type} (Uploaded by ${getUserName(file.user)})`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {isPDF ? (
            <Box
              height="70vh"
              overflowY="auto"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Document
                file={fileUrl}
                options={pdfOptions}
                onLoadSuccess={({ numPages }) => {
                  setNumPages(numPages);
                }}
                loading={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="70vh"
                  >
                    <Spinner size="xl" />
                  </Box>
                }
              >
                {Array.from(new Array(numPages), (_, index) => (
                  <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={950}
                  />
                ))}
              </Document>
            </Box>
          ) : isImage ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="70vh"
            >
              <Image
                src={fileUrl}
                alt={file.name}
                maxH="100%"
                maxW="100%"
                objectFit="contain"
                fallback={<Spinner size="xl" />}
              />
            </Box>
          ) : (
            <Alert status="info">
              <AlertIcon />
              <AlertDescription>
                File preview is not available for this file type. Please
                download to view.
              </AlertDescription>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex flex="1">
            <HStack spacing={2}>
              <Button leftIcon={<ChevronLeftIcon />} onClick={onPrevFile}>
                Previous
              </Button>
              <Button rightIcon={<ChevronRightIcon />} onClick={onNextFile}>
                Next
              </Button>
            </HStack>
          </Flex>

          {canDownload && fileUrl && (
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              mr={3}
              onClick={handleDownload}
              isLoading={isDownloading}
            >
              Download
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FilePreviewModal;
